/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/ban-ts-comment, @typescript-eslint/no-var-requires*/

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../gather-services/loading.service';
import { GatherStateService } from '../gather-services/gather-state.service';
import { GatherApiService } from '../gather-services/gather-api.service';
import { SharedDataService } from '../gather-services/shared-data.service';
import { Subscription } from 'rxjs';
import { Availability, Brand, Gather, JobPosting } from '../gather-typings';
import { TranslateService } from '@ngx-translate/core';
import { OrderStatus } from '../enums';

@Component({
    selector: 'app-gather-base',
    templateUrl: './gather-base.component.html',
    styleUrls: ['./gather-base.component.scss'],
    standalone: false
})
export class GatherBaseComponent implements OnInit, OnDestroy {
  brand: Brand;
  loading: boolean;
  showHeaderText: boolean;
  @Input() posting: JobPosting;
  private subscriptions = new Subscription();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private loadingService: LoadingService,
    private gatherStateService: GatherStateService,
    private gatherApiService: GatherApiService,
    private sharedDataService: SharedDataService,
    private translate: TranslateService
  ) {
    // @ts-ignore
    translate.setTranslation('en-US', require('../../../assets/i18n/en_us.json'));
    translate.setDefaultLang('en-US');
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.loadingService.loading$.subscribe(val => {
        this.loading = val;
      })
    );

    this.fetchRouteData();

    this.subscriptions.add(
      this.gatherStateService.brand.subscribe(brand => {
        this.brand = brand;
        this.setFavicon();
      })
    );

    this.subscriptions.add(
      this.router.events.subscribe(event => {
        this.showHeaderText = !this.router.url.includes('not-interested');
      })
    );
  }

  fetchRouteData(): void {
    this.subscriptions.add(
      this.activatedRoute.paramMap.subscribe(params => {
        const id: string = params.get('trackingId');
        this.gatherApiService.isGatherIdValid(id).subscribe(isValid => {
          if (!isValid) {
            void this.router.navigate(['gather', id, 'invalid-id']);
          } else {
            if (this.sharedDataService.gather.getValue().gatherId === -1) {
              this.gatherApiService.getGatherByToken(id).subscribe((gather: Gather) => {
                this.sharedDataService.setGather(gather);
                this.determineIfAlreadyTalent(gather);
              });
            }

            if (this.sharedDataService.jobPosting.getValue().jobPostingId === -1) {
              this.gatherApiService.getJobPostingForGather(id).subscribe((posting: JobPosting) => {
                this.sharedDataService.setJobPosting(posting);
                this.posting = posting;
                if (this.isJobClosed(posting)) {
                  void this.router.navigate(['/gather/', id, 'job-closed']);
                }
              });
            }

            if (this.sharedDataService.talentAvailability.getValue().personId === -1) {
              this.gatherApiService.getMoatsAvailability(id).subscribe((availability: Availability) => {
                this.sharedDataService.setTalentAvailability(availability);
              });
            }

            if (this.sharedDataService.trackingId.getValue() === '') {
              this.sharedDataService.setTrackingId(id);
            }
          }
        });
      })
    );
  }

  private setFavicon(): void {
    document.querySelector('link[rel="icon"]').setAttribute('href', `/assets/images/favicons/favicon-${this.brand}.ico`);
  }

  private determineIfAlreadyTalent(gather: Gather): void {
    this.subscriptions.add(
      this.gatherApiService.getTalentStatusForOrder(gather.orderId, gather.talentPersonId).subscribe(status => {
        const canRespondToGather: boolean = GatherBaseComponent.checkCandidateStatusForGather(status);
        if (!canRespondToGather) {
          this.activatedRoute.paramMap.subscribe(params => {
            const trackingId: string = params.get('trackingId');
            void this.router.navigate(['/gather/', trackingId, 'already-a-talent']);
          });
        }
      })
    );
  }

  private static checkCandidateStatusForGather(id: number): boolean {
    switch (id) {
      case 10: // Talent Applied Online
      case 20: // Contacted Talent Waiting for Response
      case 30: // Approved By Client Svc Mgr
      case 40: // Approved By Sales Mgr
      case 310: // Gather - Notification Sent
      case 342: // Gather Sent (both Email & Text)
      case 343: // Gather Sent (Text)
      case 344: // Gather Sent (Email)
      case 345: // Gather Sent (Custom Email)
      case 346: // Gather Sent (Text) - Interested
      case 347: // Gather Sent (Email) - Interested
      case 348: // Gather Sent (Custom Email) - Interested
      case 349: // Gather Sent (Text) - Not Interested
      case 350: // Gather Sent (Email) - Not Interested
      case 351: // Gather Sent (Custom Email) - Not Interested
      case 352: // Gather Sending (Text)
      case 353: // Gather Scheduled (Text)
      case 354: // Gather Failed (Text)
        return true;
      default:
        return false;
    }
  }

  private isJobClosed(jobPosting: JobPosting): boolean {
    return (
      jobPosting.orderStatusId === OrderStatus.Filled ||
      jobPosting.orderStatusId === OrderStatus.Transferred ||
      jobPosting.orderStatusId === OrderStatus.FilledByCompetitor ||
      jobPosting.orderStatusId === OrderStatus.NoSuitableTalent ||
      jobPosting.orderStatusId === OrderStatus.ClientCancelled ||
      jobPosting.orderStatusId === OrderStatus.EntryMistakeUnqualified ||
      jobPosting.orderStatusId === OrderStatus.FilledByClient
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
